import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, KeyRound, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { isIOSApp } from "@/lib/platform";
import WebsiteOnlyNotice from "@/components/home/WebsiteOnlyNotice";

const inputCls = "w-full bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-blood";

function Shell({ open, onClose, children, testId }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
          onClick={onClose} data-testid={testId}>
          <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-elevate border border-white/10 clip-frame p-8">
            <button onClick={onClose} aria-label="Close" data-testid={`${testId}-close`}
              className="absolute top-4 right-4 text-gray-400 hover:text-blood transition-colors"><X size={20} /></button>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function MemberLoginModal() {
  const { loginOpen, setLoginOpen, refresh, setPaywall } = useMember();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => { setLoginOpen(false); setStep("email"); setCode(""); };

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/members/request-code", { email });
      setStep("code");
      toast.success("Code sent — check your inbox.");
    } catch (err) {
      const status = err?.response?.status;
      toast.error(formatApiError(err));
      if (status === 403) { close(); setPaywall({ email }); }
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/members/verify", { email, code });
      await refresh();
      toast.success("Welcome back, Insider.");
      close();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Shell open={loginOpen} onClose={close} testId="member-login-modal">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-blood mb-3">Insider login</p>
      <h3 className="font-display font-black uppercase text-3xl leading-none mb-6">
        {step === "email" ? "Enter your email" : "Enter your code"}
      </h3>
      {step === "email" ? (
        <form onSubmit={requestCode} className="space-y-4">
          <input data-testid="member-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="The email you subscribed with" className={inputCls} />
          <button data-testid="member-login-send-code" type="submit" disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-blood hover:bg-red-700 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider py-3 transition-colors">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Send me a code
          </button>
          <p className="font-mono text-[11px] text-gray-500">
            Not an Insider yet?{" "}
            <button type="button" data-testid="member-login-subscribe-link" onClick={() => { close(); setPaywall({ email }); }}
              className="text-blood hover:text-white underline">Join for $1.99/mo</button>
          </p>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-4">
          <p className="text-sm text-gray-400">We emailed a 6-digit code to <span className="text-white">{email}</span>.</p>
          <input data-testid="member-login-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000"
            className={`${inputCls} text-center text-2xl tracking-[0.5em]`} />
          <button data-testid="member-login-verify" type="submit" disabled={busy || code.length !== 6}
            className="w-full flex items-center justify-center gap-2 bg-blood hover:bg-red-700 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider py-3 transition-colors">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Unlock
          </button>
          <button type="button" data-testid="member-login-back" onClick={() => setStep("email")} className="font-mono text-[11px] text-gray-500 hover:text-white">
            ← Use a different email
          </button>
        </form>
      )}
    </Shell>
  );
}

export function PaywallModal() {
  const { paywall, setPaywall, setLoginOpen } = useMember();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!paywall;

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/subscribe", { email: email || paywall?.email, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <Shell open={open} onClose={() => setPaywall(null)} testId="paywall-modal">
      <div className="w-12 h-12 flex items-center justify-center bg-blood/15 text-blood mb-5"><Lock size={22} /></div>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-blood mb-3">Insiders only</p>
      <h3 className="font-display font-black uppercase text-3xl leading-none">
        {paywall?.title ? <>Unlock <span className="text-gray-400">{paywall.title}</span></> : "Unlock the full experience"}
      </h3>
      <p className="mt-4 text-sm text-gray-400 leading-relaxed">
        Every episode, every drop and the live chat with the movement — for <span className="text-white font-bold">$1.99/month</span>. Cancel anytime.
      </p>
      {isIOSApp() ? (
        <div className="mt-6"><WebsiteOnlyNotice what="Memberships" path="/#contact" testId="ios-paywall-notice" /></div>
      ) : (
      <form onSubmit={subscribe} className="mt-6 space-y-3">
        <input data-testid="paywall-email" type="email" required value={email || paywall?.email || ""} onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com" className={inputCls} />
        <button data-testid="paywall-subscribe-button" type="submit" disabled={busy}
          className="w-full flex items-center justify-center gap-2 bg-blood hover:bg-red-700 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider py-3 transition-colors">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Become an Insider — $1.99/mo
        </button>
      </form>
      )}
      <p className="mt-4 font-mono text-[11px] text-gray-500">
        Already a member?{" "}
        <button type="button" data-testid="paywall-login-link" onClick={() => { setPaywall(null); setLoginOpen(true); }}
          className="text-blood hover:text-white underline">Log in</button>
      </p>
    </Shell>
  );
}
